import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
	RedditNotificationsDocument,
} from 'wb-reddit/graphql/generated';
import { ClientGroupedNotifications } from 'wb-reddit/app/routes/superGrid/actionRow/notifications/util';
import { orderBy } from 'lodash';
import dayjs from 'dayjs';
import { isSpotify } from '@aleph/shared-components';

const useNotificationsPolling = (isRead = false) => {
	const { data, loading, error } = useQuery(RedditNotificationsDocument, {
		pollInterval: import.meta.env.VITE_DEVELOPMENT === 'true' ? undefined : 1000 * 10,
		errorPolicy:  'all',
		fetchPolicy:  'cache-and-network',
		skip:         isSpotify(),
		variables:    {
			filters: {
				notificationIsRead: [{ value: isRead }],
			},
		},
	});

	const notifications = useMemo(
		() => (data?.notifications?.items || []).map(notif => {
			try {
				return {
					...notif,
					notificationMessage: 'Validation errors',
					validationErrors:    JSON.parse(notif.notificationMessage),
				};
			} catch {
				// JSON.Parse will fail when it's not a JSON object - only validation errors are JSON objects
				return notif;
			}
		}),
		[data],
	);

	const groupedNotifications: ClientGroupedNotifications[] = notifications.reduce<ClientGroupedNotifications[]>(
		(accu, notification) => {
			const existingGroupIdx = accu.indexOf(
				accu.find(groupedNotifs => groupedNotifs.client?.clientId === notification.client?.clientId) as ClientGroupedNotifications,
			);

			if (existingGroupIdx !== -1) {
				accu[existingGroupIdx] = {
					...accu[existingGroupIdx],
					notifications: [...(accu[existingGroupIdx]?.notifications ?? []), notification],
				};
			} else {
				accu.push({
					client:        notification.client,
					notifications: [notification],
					occurredAt:    notification.notificationCreatedAt,
				});
			}

			return accu;
		},
		[],
	);

	return {
		notifications,
		groupedNotifications: orderBy(
			groupedNotifications,
			(group) => dayjs(group.occurredAt).toDate(),
			'desc',
		),
		notificationsLoaded: useMemo(() => !!data?.notifications?.items, [data]),
		loading,
		error,
	};
};

export default useNotificationsPolling;
